<template>
  <div class="flex w-full h-full relative">
    <Line
      :data="chartData"
      :options="chartOptions"
      :chart-id="chartId"
      :dataset-id-key="datasetIdKey"
      :plugins="plugins"
      :css-classes="cssClasses"
      :styles="styles"
      :width="width"
      :height="height"
    />
  </div>
</template>

<script setup lang="ts">
import { useStoreApiAction } from '@/composables/useStoreApiAction';
import { australianCurrency, cumulativeSum, roundDecimals } from '@/helpers';
import { t } from '@/i18n';
import Project from '@/models/Project';
import Trade from '@/models/Trade';
import { useProjectsStore } from '@/store/projects';
import { useTradesStore } from '@/store/trades';
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import { DateTime } from 'luxon';
import { computed, ref, watch } from 'vue';
import { Line } from 'vue-chartjs';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

const props = defineProps<{
  projectId: string;
}>();

const projectsStore = useProjectsStore();
const tradesStore = useTradesStore();

const project = computed(() => {
  return projectsStore.project;
});

const trades = computed(() => {
  return tradesStore.models
    .with('trade_items')
    .where('project_id', parseInt(props.projectId))
    .where('boq_type', 'direct')
    .orderBy('sort_order')
    .whereHas('trade_items')
    .get();
});

// Chart configuration
const chartId = 'cashflow-forecast-chart';
const datasetIdKey = 'label';
const plugins = [];
const cssClasses = '';
const styles = {};
const width = undefined;
const height = undefined;
// Store for forecast overrides
const forecastOverrides = ref<Map<number, number[]>>(new Map());

const calculateForecastData = () => {
  if(!project.value) return [];

  const monthsInProject = project.value.contractMonths;
  const monthlyTotals = [];

  monthsInProject.forEach((projectMonth, i) => {
    monthlyTotals[i] = 0;

    trades.value.filter((trade) => {
      return trade.forecast_start_date && trade.forecast_end_date && trade.forecast;
    }).forEach((trade) => {
      if(trade.forecastStart <= projectMonth && projectMonth <= trade.forecastEnd) {
        const monthIndex = Math.round(Math.abs(trade.forecastStart.diff(projectMonth, ['months']).months));
        // Use override if available, otherwise use original forecast
        const override = forecastOverrides.value.get(trade.id);

        const forecastValue = override ?
          (override[monthIndex] ?? 0) :
          (trade.forecast[monthIndex] ?? 0);

        monthlyTotals[i] += roundDecimals(forecastValue);
      }
    });
  });

  return cumulativeSum(monthlyTotals);
};

const calculateBaselineData = () => {
  if(!project.value) return [];

  const monthsInProject = project.value.contractMonths;
  const monthlyTotals = [];

  monthsInProject.forEach((projectMonth, i) => {
    monthlyTotals[i] = 0;

    trades.value.filter((trade) => {
      return trade.baseline_start_date && trade.baseline_end_date;
    }).forEach((trade) => {
      if(trade.baselineStart <= projectMonth && projectMonth <= trade.baselineEnd) {
        const monthIndex = Math.round(Math.abs(trade.baselineStart.diff(projectMonth, ['months']).months));

        monthlyTotals[i] += roundDecimals(
          trade.baseline_forecast[monthIndex] ?? 0,
        );
      }
    });
  });

  return cumulativeSum(monthlyTotals);
};

const chartData = computed(() => {
  if(!project.value) {
    return {
      labels: [],
      datasets: [],
    };
  }

  const contractLength = project.value.contractLength;

  const labels = Array.from({ length: contractLength }, (_, i) => {
    const date = DateTime.fromJSDate(project.value.contractStartDate).startOf('month');

    return date.plus({ months: i }).toFormat('MMM yyyy');
  });

  return {
    labels,
    datasets: [
      {
        label: t('HeadContract.CashflowForecasting.CashflowChart.forecast'),
        data: calculateForecastData(),
        fill: false,
        borderColor: '#3B82F6',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        borderWidth: 3,
        pointBackgroundColor: '#3B82F6',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        pointRadius: 6,
        pointHoverRadius: 8,
        tension: 0.4,
        order: 1,
      },
      {
        label: t('HeadContract.CashflowForecasting.CashflowChart.baseline'),
        data: calculateBaselineData(),
        fill: false,
        borderColor: '#EF4444',
        backgroundColor: 'rgba(239, 68, 68, 0.1)',
        borderWidth: 3,
        pointBackgroundColor: '#EF4444',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        pointRadius: 6,
        pointHoverRadius: 8,
        tension: 0.4,
        order: 2,
      },
    ],
  };
});

const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    intersect: false,
    mode: 'index' as const,
  },

  plugins: {
    legend: {
      position: 'bottom' as const,
      labels: {
        usePointStyle: true,
        padding: 8,
        font: {
          size: 12,
          weight: '600',
        },
      },
    },

    tooltip: {
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      titleColor: '#ffffff',
      bodyColor: '#ffffff',
      borderColor: 'rgba(255, 255, 255, 0.1)',
      borderWidth: 1,
      cornerRadius: 8,
      displayColors: true,
      callbacks: {
        label(context: any) {
          return context.dataset.label + ': ' + australianCurrency(context.parsed.y);
        },
      },
    },
  },

  scales: {
    x: {
      grid: {
        color: 'rgba(0, 0, 0, 0.05)',
      },

      ticks: {
        font: {
          size: 11,
        },

        color: '#6B7280',
      },
    },

    y: {
      grid: {
        color: 'rgba(0, 0, 0, 0.05)',
      },

      ticks: {
        font: {
          size: 11,
        },

        color: '#6B7280',
        callback(value: any) {
          return australianCurrency(value);
        },
      },
    },
  },

  elements: {
    point: {
      hoverBackgroundColor: '#ffffff',
    },
  },
}));

// Expose methods for parent component
const updateForecastOverride = (tradeToOverride: Trade, newForecasts: number[]) => {
  forecastOverrides.value.set(tradeToOverride.id, newForecasts);
};

const clearForecast = (trade: Trade) => {
  forecastOverrides.value.delete(trade.id);
};

watch([() => project.value?.id], (newValue, oldValue) => {
  if(newValue !== oldValue) {
    forecastOverrides.value.clear();
  }
}, { immediate: true });

defineExpose({
  updateForecastOverride,
  clearForecast,
});
</script>

<style scoped></style>
